import { Component } from '@theme/component';
import { ThemeEvents, CartErrorEvent, CartAddEvent } from '@theme/events';

/**
 * @typedef {Object} GiftCardRecipientFormRefs
 * @property {HTMLInputElement} myEmailButton - Button for selecting my email option
 * @property {HTMLInputElement} recipientEmailButton - Button for selecting recipient email option
 * @property {HTMLDivElement} recipientFields - Container for recipient form fields
 * @property {HTMLInputElement} senderName - Sender name input field
 * @property {HTMLInputElement} recipientEmail - Recipient email input field
 * @property {HTMLInputElement} confirmRecipientEmail - Confirm recipient email input field
 * @property {HTMLInputElement} recipientName - Recipient name input field
 * @property {HTMLTextAreaElement} recipientMessage - Recipient message textarea
 * @property {HTMLInputElement} [timezoneOffset] - Timezone offset hidden input (optional)
 * @property {HTMLInputElement} [controlFlag] - Shopify gift card control flag (optional as it's dynamically queried)
 * @property {HTMLDivElement} [senderNameError] - Sender name error message container (optional)
 * @property {HTMLDivElement} [confirmEmailError] - Confirm email error message container (optional)
 * @property {HTMLDivElement} [emailError] - Email error message container (optional)
 * @property {HTMLDivElement} [nameError] - Name error message container (optional)
 * @property {HTMLDivElement} [messageError] - Message error message container (optional)
 * @property {HTMLSpanElement} [characterCount] - Character count display element (optional)
 * @property {HTMLDivElement} [liveRegion] - Live region for screen reader announcements (optional)
 */

/**
 * @extends {Component<GiftCardRecipientFormRefs>}
 */
class GiftCardRecipientForm extends Component {
  static DeliveryMode = {
    SELF: 'self', // Send to my email
    RECIPIENT: 'recipient_form', // Send to recipient's email with form
  };

  #currentMode = GiftCardRecipientForm.DeliveryMode.RECIPIENT;

  // Store bound event handlers for cleanup
  /** @type {(() => void) | null} */
  #updateCharacterCountBound = null;
  /** @type {((event: Event) => void) | null} */
  #displayCartErrorBound = null;
  /** @type {(() => void) | null} */
  #cartAddEventBound = null;
  /** @type {((event: Event) => void) | null} */
  #validateEmailMatchBound = null;
  /** @type {(() => void) | null} */
  #validateRequiredFieldsBound = null;
  /** @type {(() => void) | null} */
  #validateSenderNameBound = null;
  /** @type {(() => void) | null} */
  #validateRecipientNameBound = null;
  /** @type {(() => void) | null} */
  #validateRecipientEmailBound = null;
  /** @type {(() => void) | null} */
  #validateConfirmEmailBound = null;


  requiredRefs = [
    'myEmailButton',
    'recipientEmailButton',
    'recipientFields',
    'senderName',
    'recipientEmail',
    'confirmRecipientEmail',
    'recipientName',
    'recipientMessage',
  ];

  /**
   * Get all recipient input fields
   * @returns {(HTMLInputElement | HTMLTextAreaElement)[]} Array of input fields
   */
  get #inputFields() {
    return [this.refs.senderName, this.refs.recipientEmail, this.refs.confirmRecipientEmail, this.refs.recipientName, this.refs.recipientMessage];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeForm();

    this.#updateCharacterCountBound = () => this.#updateCharacterCount();
    this.refs.recipientMessage.addEventListener('input', this.#updateCharacterCountBound);

    this.#displayCartErrorBound = this.#displayCartError.bind(this);
    // @ts-ignore - #displayCartErrorBound is guaranteed to be non-null here
    document.addEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);

    this.#cartAddEventBound = () => this.#handleCartAdd();
    document.addEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);

    // Add email validation
    this.#validateEmailMatchBound = () => this.#validateEmailMatch();
    this.refs.recipientEmail.addEventListener('input', this.#validateEmailMatchBound);
    this.refs.recipientEmail.addEventListener('blur', this.#validateEmailMatchBound);
    this.refs.confirmRecipientEmail.addEventListener('input', this.#validateEmailMatchBound);
    this.refs.confirmRecipientEmail.addEventListener('blur', this.#validateEmailMatchBound);

    // Add validation for all required fields to enable/disable submit button
    this.#validateRequiredFieldsBound = () => this.#validateRequiredFields();
    this.refs.senderName.addEventListener('input', this.#validateRequiredFieldsBound);
    this.refs.recipientName.addEventListener('input', this.#validateRequiredFieldsBound);
    this.refs.recipientEmail.addEventListener('input', this.#validateRequiredFieldsBound);
    this.refs.confirmRecipientEmail.addEventListener('input', this.#validateRequiredFieldsBound);

    // Add individual field validation on input and blur to show "required" messages
    this.#validateSenderNameBound = () => this.#validateSenderName();
    this.refs.senderName.addEventListener('input', this.#validateSenderNameBound);
    this.refs.senderName.addEventListener('blur', this.#validateSenderNameBound);

    this.#validateRecipientNameBound = () => this.#validateRecipientName();
    this.refs.recipientName.addEventListener('input', this.#validateRecipientNameBound);
    this.refs.recipientName.addEventListener('blur', this.#validateRecipientNameBound);

    this.#validateRecipientEmailBound = () => this.#validateRecipientEmail();
    this.refs.recipientEmail.addEventListener('input', this.#validateRecipientEmailBound);
    this.refs.recipientEmail.addEventListener('blur', this.#validateRecipientEmailBound);

    this.#validateConfirmEmailBound = () => this.#validateConfirmEmail();
    this.refs.confirmRecipientEmail.addEventListener('input', this.#validateConfirmEmailBound);
    this.refs.confirmRecipientEmail.addEventListener('blur', this.#validateConfirmEmailBound);

  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#updateCharacterCountBound) {
      this.refs.recipientMessage.removeEventListener('input', this.#updateCharacterCountBound);
      this.#updateCharacterCountBound = null;
    }

    if (this.#displayCartErrorBound) {
      document.removeEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);
      this.#displayCartErrorBound = null;
    }

    if (this.#cartAddEventBound) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#cartAddEventBound);
      this.#cartAddEventBound = null;
    }

    if (this.#validateEmailMatchBound) {
      this.refs.recipientEmail.removeEventListener('input', this.#validateEmailMatchBound);
      this.refs.recipientEmail.removeEventListener('blur', this.#validateEmailMatchBound);
      this.refs.confirmRecipientEmail.removeEventListener('input', this.#validateEmailMatchBound);
      this.refs.confirmRecipientEmail.removeEventListener('blur', this.#validateEmailMatchBound);
      this.#validateEmailMatchBound = null;
    }

    if (this.#validateRequiredFieldsBound) {
      this.refs.senderName.removeEventListener('input', this.#validateRequiredFieldsBound);
      this.refs.recipientName.removeEventListener('input', this.#validateRequiredFieldsBound);
      this.refs.recipientEmail.removeEventListener('input', this.#validateRequiredFieldsBound);
      this.refs.confirmRecipientEmail.removeEventListener('input', this.#validateRequiredFieldsBound);
      this.#validateRequiredFieldsBound = null;
    }

    if (this.#validateSenderNameBound) {
      this.refs.senderName.removeEventListener('input', this.#validateSenderNameBound);
      this.refs.senderName.removeEventListener('blur', this.#validateSenderNameBound);
      this.#validateSenderNameBound = null;
    }

    if (this.#validateRecipientNameBound) {
      this.refs.recipientName.removeEventListener('input', this.#validateRecipientNameBound);
      this.refs.recipientName.removeEventListener('blur', this.#validateRecipientNameBound);
      this.#validateRecipientNameBound = null;
    }

    if (this.#validateRecipientEmailBound) {
      this.refs.recipientEmail.removeEventListener('input', this.#validateRecipientEmailBound);
      this.refs.recipientEmail.removeEventListener('blur', this.#validateRecipientEmailBound);
      this.#validateRecipientEmailBound = null;
    }

    if (this.#validateConfirmEmailBound) {
      this.refs.confirmRecipientEmail.removeEventListener('input', this.#validateConfirmEmailBound);
      this.refs.confirmRecipientEmail.removeEventListener('blur', this.#validateConfirmEmailBound);
      this.#validateConfirmEmailBound = null;
    }
  }

  /**
   * Initialize form with default state, self delivery is selected by default
   */
  #initializeForm() {
    this.#updateButtonStates(GiftCardRecipientForm.DeliveryMode.RECIPIENT);

    this.refs.recipientFields.hidden = false;

    this.#clearRecipientFields();
    // this.#disableRecipientFields();

    const parentForm = this.closest('form');
    const addToCartButton = parentForm?.querySelector('button[type="submit"]');
    if (addToCartButton) {
        // @ts-ignore
        addToCartButton.disabled = true;
    }
  }

  /**
   * Handle toggle between my email and recipient email
   * @param {string} mode - Delivery mode (either 'self' or 'recipient_form')
   * @param {Event} _event - Change event (unused)
   */
  toggleRecipientForm(mode, _event) {
    // Validate mode
    if (!Object.values(GiftCardRecipientForm.DeliveryMode).includes(mode)) {
      throw new Error(
        `Invalid delivery mode: ${mode}. Must be one of: ${Object.values(GiftCardRecipientForm.DeliveryMode).join(
          ', '
        )}`
      );
    }

    if (this.#currentMode === mode) return;
    this.#currentMode = mode;

    this.#updateFormState();
  }

  /**
   * Update form state based on current mode
   */
  #updateFormState() {
    const { DeliveryMode } = GiftCardRecipientForm;
    const isRecipientMode = this.#currentMode === DeliveryMode.RECIPIENT;

    this.#updateButtonStates(this.#currentMode);

    this.refs.recipientFields.hidden = !isRecipientMode;

    if (isRecipientMode) {

      this.#enableRecipientFields();

      this.#updateCharacterCount();

      // Announce to screen readers
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_visible || 'Recipient form fields are now visible';
      }

      // Focus first field for accessibility
      this.refs.recipientEmail.focus();
    } else {
      this.#clearRecipientFields();
      this.#disableRecipientFields();

      // Announce to screen readers
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent =
          Theme.translations?.recipient_form_fields_hidden || 'Recipient form fields are now hidden';
      }
    }

    this.dispatchEvent(
      new CustomEvent('recipient:toggle', {
        detail: {
          mode: this.#currentMode,
          recipientFormVisible: isRecipientMode,
        },
        bubbles: true,
      })
    );
  }

  /**
   * Update radio button states
   * @param {string} mode - Current delivery mode
   */
  #updateButtonStates(mode) {
    const { DeliveryMode } = GiftCardRecipientForm;

    switch (mode) {
      case DeliveryMode.SELF:
        this.refs.myEmailButton.checked = true;
        this.refs.recipientEmailButton.checked = true;
        break;

      case DeliveryMode.RECIPIENT:
        this.refs.myEmailButton.checked = false;
        this.refs.recipientEmailButton.checked = true;
        break;

      default:
        console.warn(`Unknown delivery mode: ${mode}`);
        // Default to self delivery
         this.refs.myEmailButton.checked = false;
         this.refs.recipientEmailButton.checked = true;
    }
  }

  /**
   * Clear all recipient form fields
   */
  #clearRecipientFields() {
    for (const field of this.#inputFields) {
      field.value = '';
    }

    this.#updateCharacterCount();
    this.#clearErrorMessages();
  }

  /**
   * Disable recipient form fields when sending to self
   */
  #disableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = true;
      field.removeAttribute('required');
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    // Remove control field when sending to self
    const controlFlag = this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]');
    if (controlFlag) {
      controlFlag.remove();
    }

    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = true;
      this.refs.timezoneOffset.value = '';
    }

    this.#clearErrorMessages();
  }

  /**
   * Enable recipient form fields when sending to recipient
   */
  #enableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = false;
      // Set required on all fields except message
      if (field !== this.refs.recipientMessage) {
        field.setAttribute('required', 'required');
      }
    }

    // Add control field when sending to recipient
    let controlFlag = this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]');
    if (!controlFlag) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[__shopify_send_gift_card_to_recipient]';
      input.value = 'on';
      this.appendChild(input);
    }

    // Enable and set timezone offset
    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = false;
      this.refs.timezoneOffset.value = new Date().getTimezoneOffset().toString();
    }

  }

  /**
   * Validate that email and confirm email match
   * @returns {boolean} True if emails match or confirm email is empty, false otherwise
   */
  #validateEmailMatch() {
    const email = this.refs.recipientEmail.value.trim();
    const confirmEmail = this.refs.confirmRecipientEmail.value.trim();

    // Only show error if confirm email has been filled out
    if (confirmEmail.length === 0) {
      this.#hideConfirmEmailError();
      return true;
    }

    if (email !== confirmEmail) {
      this.#showConfirmEmailError('Email addresses do not match');
      return false;
    } else {
      this.#hideConfirmEmailError();
      return true;
    }
  }

  /**
   * Show confirm email error message
   * @param {string} message - Error message to display
   */
  #showConfirmEmailError(message) {
    if (this.refs.confirmEmailError) {
      const errorTextElement = this.refs.confirmEmailError.querySelector('span');
      if (errorTextElement) {
        errorTextElement.textContent = message;
      }
      this.refs.confirmEmailError.classList.remove('hidden');
    }

    this.refs.confirmRecipientEmail.setAttribute('aria-invalid', 'true');
    const errorId = `RecipientForm-confirm-email-error-${this.dataset.sectionId || 'default'}`;
    this.refs.confirmRecipientEmail.setAttribute('aria-describedby', errorId);
  }

  /**
   * Hide confirm email error message
   */
  #hideConfirmEmailError() {
    if (this.refs.confirmEmailError) {
      this.refs.confirmEmailError.classList.add('hidden');
      const errorTextElement = this.refs.confirmEmailError.querySelector('span');
      if (errorTextElement) {
        errorTextElement.textContent = '';
      }
    }

    this.refs.confirmRecipientEmail.removeAttribute('aria-invalid');
    this.refs.confirmRecipientEmail.removeAttribute('aria-describedby');
  }

  /**
   * Validate all required fields and update add to cart button state
   */
  #validateRequiredFields() {
    const parentForm = this.closest('form');
    const addToCartButton = parentForm?.querySelector('button[type="submit"]');

    if (!addToCartButton) return;

    // Check if all required fields are filled
    const senderNameFilled = this.refs.senderName.value.trim().length > 0;
    const recipientNameFilled = this.refs.recipientName.value.trim().length > 0;
    const recipientEmailFilled = this.refs.recipientEmail.value.trim().length > 0;
    const confirmEmailFilled = this.refs.confirmRecipientEmail.value.trim().length > 0;

    // Check if emails match (only if both are filled)
    const emailsMatch = this.#validateEmailMatch();

    // Enable button only if all required fields are filled and emails match
    const allFieldsValid = senderNameFilled && recipientNameFilled && recipientEmailFilled && confirmEmailFilled && emailsMatch;

    // @ts-ignore
    addToCartButton.disabled = !allFieldsValid;
  }

  /**
   * Validate sender name field
   */
  #validateSenderName() {
    const value = this.refs.senderName.value.trim();

    if (value.length === 0) {
      this.#showFieldError('senderNameError', 'Sender name is required');
      this.refs.senderName.setAttribute('aria-invalid', 'true');
    } else {
      this.#hideFieldError('senderNameError');
      this.refs.senderName.removeAttribute('aria-invalid');
    }
  }

  /**
   * Validate recipient name field
   */
  #validateRecipientName() {
    const value = this.refs.recipientName.value.trim();

    if (value.length === 0) {
      this.#showFieldError('nameError', 'Recipient name is required');
      this.refs.recipientName.setAttribute('aria-invalid', 'true');
    } else {
      this.#hideFieldError('nameError');
      this.refs.recipientName.removeAttribute('aria-invalid');
    }
  }

  /**
   * Validate recipient email field
   */
  #validateRecipientEmail() {
    const value = this.refs.recipientEmail.value.trim();

    if (value.length === 0) {
      this.#showFieldError('emailError', 'Recipient email is required');
      this.refs.recipientEmail.setAttribute('aria-invalid', 'true');
    } else if (!this.#isValidEmail(value)) {
      this.#showFieldError('emailError', 'Please enter a valid email address');
      this.refs.recipientEmail.setAttribute('aria-invalid', 'true');
    } else {
      this.#hideFieldError('emailError');
      this.refs.recipientEmail.removeAttribute('aria-invalid');
    }
  }

  /**
   * Validate confirm email field
   */
  #validateConfirmEmail() {
    const value = this.refs.confirmRecipientEmail.value.trim();

    if (value.length === 0) {
      this.#showFieldError('confirmEmailError', 'Confirmation email is required');
      this.refs.confirmRecipientEmail.setAttribute('aria-invalid', 'true');
    } else if (!this.#isValidEmail(value)) {
      this.#showFieldError('confirmEmailError', 'Please enter a valid email address');
      this.refs.confirmRecipientEmail.setAttribute('aria-invalid', 'true');
    } else {
      // Don't hide error here if emails don't match - let #validateEmailMatch handle it
      if (this.refs.recipientEmail.value.trim() === value) {
        this.#hideFieldError('confirmEmailError');
        this.refs.confirmRecipientEmail.removeAttribute('aria-invalid');
      }
    }
  }

  /**
   * Validate email format
   * @param {string} email - Email address to validate
   * @returns {boolean} True if email is valid, false otherwise
   */
  #isValidEmail(email) {
    // Use the same pattern as in the HTML input
    const emailPattern = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  }

  /**
   * Show error message for a field
   * @param {string} errorRef - The ref name of the error container
   * @param {string} message - Error message to display
   */
  #showFieldError(errorRef, message) {
    const errorContainer = this.refs[errorRef];
    if (errorContainer && errorContainer instanceof HTMLElement) {
      const errorTextElement = errorContainer.querySelector('span');
      if (errorTextElement) {
        errorTextElement.textContent = message;
      }
      errorContainer.classList.remove('hidden');
    }
  }

  /**
   * Hide error message for a field
   * @param {string} errorRef - The ref name of the error container
   */
  #hideFieldError(errorRef) {
    const errorContainer = this.refs[errorRef];
    if (errorContainer && errorContainer instanceof HTMLElement) {
      errorContainer.classList.add('hidden');
      const errorTextElement = errorContainer.querySelector('span');
      if (errorTextElement) {
        errorTextElement.textContent = '';
      }
    }
  }

  /**
   * Update character count display
   */
  #updateCharacterCount() {
    if (!this.refs.characterCount) return;

    const currentLength = this.refs.recipientMessage.value.length;
    const maxLength = this.refs.recipientMessage.maxLength;

    const template = this.refs.characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    this.refs.characterCount.textContent = updatedText;
  }

  /**
   * Handles cart error events
   * @param {CartErrorEvent} event - The cart error event
   */
  #displayCartError(event) {
    if (event.detail?.data) {
      const { message, errors, description } = event.detail.data;

      // Display the error message
      if (errors && typeof errors === 'object') {
        this.#displayErrorMessage(message || 'There was an error', errors);
      } else if (message) {
        this.#displayErrorMessage(message, description);
      }
    }
  }

  /**
   * Display error messages in the appropriate error containers
   * @param {string} title - The main error message title
   * @param {Object} body - Error details
   */
  #displayErrorMessage(title, body) {
    this.#clearErrorMessages();

    if (typeof body === 'object' && body !== null) {
      /** @type {Record<string, {inputRef: string, errorRef: string}>} */
      const fieldMap = {
        email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
        name: { inputRef: 'recipientName', errorRef: 'nameError' },
        message: { inputRef: 'recipientMessage', errorRef: 'messageError' },
      };

      for (const [field, errorMessages] of Object.entries(body)) {
        const fieldConfig = fieldMap[field];
        if (!fieldConfig) continue;

        const { inputRef, errorRef } = fieldConfig;
        const errorContainer = this.refs[errorRef];
        const inputElement = this.refs[inputRef];

        if (errorContainer && errorContainer instanceof HTMLElement) {
          const errorTextElement = errorContainer.querySelector('span');
          if (errorTextElement) {
            const message = Array.isArray(errorMessages) ? errorMessages.join(', ') : errorMessages;
            errorTextElement.textContent = `${message}.`;
          }

          errorContainer.classList.remove('hidden');
        }

        if (inputElement && inputElement instanceof HTMLElement) {
          // Set ARIA attributes for accessibility
          inputElement.setAttribute('aria-invalid', 'true');
          const errorId = `RecipientForm-${field}-error-${this.dataset.sectionId || 'default'}`;
          inputElement.setAttribute('aria-describedby', errorId);
        }
      }
    }

    // Announce errors to screen readers
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent =
        title || Theme.translations?.recipient_form_error || 'There was an error with the form submission';
    }
  }

  /**
   * Clear all error messages and reset ARIA attributes
   */
  #clearErrorMessages() {
    // List of error container refs
    const errorRefs = ['senderNameError', 'emailError', 'confirmEmailError', 'nameError', 'messageError'];

    for (const errorRef of errorRefs) {
      const errorContainer = this.refs[errorRef];
      if (errorContainer && errorContainer instanceof HTMLElement) {
        errorContainer.classList.add('hidden');
        const errorTextElement = errorContainer.querySelector('span');
        if (errorTextElement) {
          errorTextElement.textContent = '';
        }
      }
    }

    // Remove ARIA attributes from all input fields
    for (const field of this.#inputFields) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    // Clear live region announcement
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = '';
    }
  }

  #handleCartAdd() {
    this.#clearErrorMessages();
  }
}

// Register the custom element
customElements.define('gift-card-recipient-form', GiftCardRecipientForm);
